import React, { useMemo } from 'react';
import { Layout, Divider } from 'antd';
import useSWR from 'swr';
import styled from 'styled-components';

const { Content } = Layout;

import { moneyFormatter, useHandleSWR } from '~/libs/helpers'
import { BlockLayout } from './layouts';
import useGlobalState from '~/libs/context';
import { CheckOutlined } from '@ant-design/icons';

const StyledSection = styled.div`
  display: flex;
  margin-top: 0px;
  flex-direction: column;
	padding: 8px 0px;
	width: 100%;
`;

const StyledLine = styled.div`
  display: flex;
  width: 100%;
  padding: 0px 2px;

  .title {
    flex: 3;
  }

  .amount {
    flex: 1;
    text-align: right;
  }
`;

const BalanceSheet = () => {
	const { year } = useGlobalState(); 
	const { data: swrBalanceSheet } = useSWR(`/api/metrics/balanceSheet?year=${year}`);
  const { data: balanceSheet } = useHandleSWR(swrBalanceSheet, { assets: [], liabilities: [], totals: [] }, true);

  // TODO: Maybe we should find a way to get the totals directly from balanceSheet to avoid two requests
  const { data: swrProfitLoss } = useSWR(`/api/metrics/profitLoss?year=${year}`);
  const { data: profitLoss } = useHandleSWR(swrProfitLoss, { totals: [] }, true);
  
  const jsxMonths = useMemo(() => {
    return <StyledLine>
     <div className='title'></div>
     {Array.from({ length: 12 }, (_, i) => <div key={'balance-month-' + (i + 1)} className="amount">{i + 1}月</div>)}
     <div className="amount">Total</div>
    </StyledLine>;
	}, []);

  const jsxAssets = useMemo(() => {
    return balanceSheet?.assets.map(entry => 
      <StyledLine key={'balance-' + entry.account.name}>
        <div className='title'>{entry.account.title}</div>
        {entry.months.map((x, i) => <div key={'balance-' + entry.account.name + '-' + i} className="amount">{moneyFormatter(x)}</div>)}
        <div key={'balance-' + entry.account.name} className="amount">{moneyFormatter(entry.total)}</div>
      </StyledLine>
    );
	}, [balanceSheet]);
  
  const jsxAssetsTotals = useMemo(() => {
    const months = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    for (let asset of balanceSheet?.assets) {
      for (let c = 0; c < asset.months.length; c++) {
        months[c] += asset.months[c];
      }
    }
    const total = months.reduce((a, b) => a + b, 0);
    return <StyledLine>
      <div className='title'>Totals</div>
      {months.map((x, i) => <div key={'balance-total-' + (i + 1)} className="amount">{moneyFormatter(x)}</div>)}
      <div className='amount'>{moneyFormatter(total)}</div>
    </StyledLine>;
  }, [balanceSheet]);

	const jsxLiabilities = useMemo(() => {
    return balanceSheet?.liabilities.map(entry => 
      <StyledLine key={'balance-' + entry.account.name}>
        <div className='title'>{entry.account.title}</div>
        {entry.months.map((x, i) => <div key={'balance-' + entry.account.name + '-' + i} className="amount">{moneyFormatter(x)}</div>)}
        <div key={'balance-' + entry.account.name} className="amount">{moneyFormatter(entry.total)}</div>
      </StyledLine>
    );
  }, [balanceSheet]);

  const jsxLiabilitiesTotals = useMemo(() => {
    const months = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    for (let asset of balanceSheet?.liabilities) {
      for (let c = 0; c < asset.months.length; c++) {
        months[c] += asset.months[c];
      }
    }
    const total = months.reduce((a, b) => a + b, 0);
    return <StyledLine>
      <div className='title'>Totals</div>
      {months.map((x, i) => <div key={'balance-total-' + (i + 1)} className="amount">{moneyFormatter(x)}</div>)}
      <div className='amount'>{moneyFormatter(total)}</div>
    </StyledLine>;
  }, [balanceSheet]);

  const jsxMonthlyTotals = useMemo(() => {
    return <StyledLine>
     <div className='title'>Totals</div>
     {balanceSheet?.totals.map((x, i) => 
      <div key={'pnl-' + (i + 1)} className="amount">
        {x === 0 && <CheckOutlined />}
        {x !== 0 && moneyFormatter(x)}
      </div>)}
    <div className='amount'>{moneyFormatter(balanceSheet.total)}</div>
    </StyledLine>;
	}, [balanceSheet]);

	return (
    <BlockLayout>
      <Content>
        <StyledSection>
          {jsxMonths}
        </StyledSection>

        <Divider orientation='left'>Assets</Divider>
        <StyledSection>
          {jsxAssets}
        </StyledSection>
        <StyledSection>
          {jsxAssetsTotals}
        </StyledSection>

        <Divider orientation='left'>Liabilities</Divider>
        <StyledSection>
          {jsxLiabilities}
        </StyledSection>
        <StyledSection>
          {jsxLiabilitiesTotals}
        </StyledSection>

        <Divider></Divider>

        <StyledSection>
          {jsxMonthlyTotals}
        </StyledSection>
      </Content>
    </BlockLayout>
	);

};

export default BalanceSheet;